// src/components/PendingOrderProgress.jsx
//
// Visual progress bar for partial fills on a pending limit order.
// Reads:
//   - pending_order_quantity   (total qty ordered)
//   - pending_order_applied_qty (qty filled so far, from _appliedQty)
//   - pending_order_side       (BUY / SELL / SHORT_OPEN / SHORT_CLOSE)
//   - pending_order_price
//
// All of these ride on the existing botUpdate:{N} WS channel.
//
// Renders nothing if there's no pending order. Renders a thin progress
// bar when a pending order exists, full red/green when it's complete.
//
// Sits in the bot card header, between the bot info and the action buttons.

import React from 'react';
import { Box, LinearProgress, Typography, Tooltip, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, HourglassEmpty } from '@mui/icons-material';


export function PendingOrderProgress({ bot, quote }) {
  if (!bot?.pendingOrderId) return null;

  const totalQty = num(bot.pendingOrderQuantity);
  const appliedQty = num(bot.pendingOrderAppliedQty);
  const side = String(bot.pendingOrderSide || '').toUpperCase();
  const price = num(bot.pendingOrderPrice);

  // No qty info yet — show indeterminate pending state
  if (totalQty <= 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 0.5 }}>
        <HourglassEmpty sx={{ fontSize: 14, color: '#d9a441' }} />
        <Typography variant="caption" sx={{ color: '#d9a441', fontWeight: 600, fontSize: '0.7rem' }}>
          {side} order pending
          {price > 0 && ` @ ${price.toFixed(2)} ${quote || ''}`}
        </Typography>
      </Box>
    );
  }

  const fillPct = totalQty > 0 ? Math.min(100, (appliedQty / totalQty) * 100) : 0;
  const isBuyLike = side === 'BUY' || side === 'SHORT_CLOSE';
  const accentColor = isBuyLike ? '#46b88a' : '#d97b72';
  const icon = isBuyLike
    ? <TrendingUp sx={{ fontSize: 14, color: accentColor }} />
    : <TrendingDown sx={{ fontSize: 14, color: accentColor }} />;

  const isFullyFilled = appliedQty >= totalQty;
  const hasPartial = appliedQty > 0 && !isFullyFilled;

  return (
    <Tooltip
      title={
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" fontWeight={600}>
            {hasPartial ? 'Partial Fill in Progress' : 'Order Pending'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption">Filled:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {appliedQty.toFixed(6)} / {totalQty.toFixed(6)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Remaining:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {(totalQty - appliedQty).toFixed(6)}
            </Typography>
          </Box>
          {price > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">Limit Price:</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {price.toFixed(2)} {quote || ''}
              </Typography>
            </Box>
          )}
        </Box>
      }
      arrow
    >
      <Box sx={{ mt: 1, mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {icon}
          <Typography variant="caption" sx={{ color: accentColor, fontWeight: 600, fontSize: '0.7rem' }}>
            {side} {totalQty.toFixed(4)}
            {price > 0 && ` @ ${price.toFixed(2)}`}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9aa6bd', fontFamily: 'monospace', fontSize: '0.65rem', ml: 'auto' }}>
            {hasPartial && `${appliedQty.toFixed(4)} / ${totalQty.toFixed(4)}`}
            {hasPartial && ' '}
            ({fillPct.toFixed(0)}%)
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={fillPct}
          sx={{
            height: 4,
            borderRadius: 2,
            bgcolor: '#0f1218',
            '& .MuiLinearProgress-bar': {
              bgcolor: accentColor,
              borderRadius: 2,
            },
          }}
        />
      </Box>
    </Tooltip>
  );
}


function num(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}